/**
 * MIT License
 * Copyright (c) 2023 Jamasp Team
 */
import { useState } from 'react';
import PropTypes from 'prop-types';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

function mask(str) {
  if (!str) return null;
  if (str.length <= 6) return '••••••';
  return str.slice(0, 4) + '•'.repeat(Math.min(str.length - 6, 8)) + str.slice(-2);
}

function ProlificCell({ prolificId }) {
  const [revealed, setRevealed] = useState(false);
  if (!prolificId) return <span style={{ color: 'var(--jp-gray-400)' }}>N/A</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontFamily: 'var(--jp-mono)', fontSize: 12, letterSpacing: revealed ? 0 : '0.05em' }}>
        {revealed ? prolificId : mask(prolificId)}
      </span>
      <button
        onClick={() => setRevealed((r) => !r)}
        title={revealed ? 'Mask ID' : 'Reveal ID'}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
          color: 'var(--jp-gray-400)', fontSize: 13, lineHeight: 1,
          borderRadius: 4, transition: 'color 100ms',
        }}
        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--jp-blue-mid)'}
        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--jp-gray-400)'}
      >
        {revealed ? '🙈' : '👁'}
      </button>
    </span>
  );
}

ProlificCell.propTypes = {
  prolificId: PropTypes.string,
};

/** Google Health connection status, as the participant page shows it. */
function HealthCell({ health }) {
  if (health?.connected) return <span className="ja-chip ja-chip-active">connected</span>;
  return <span className="ja-chip ja-chip-inactive">{health?.status || 'not connected'}</span>;
}

HealthCell.propTypes = {
  health: PropTypes.shape({ connected: PropTypes.bool, status: PropTypes.string }),
};

const FETCH_LABEL = {
  queued: ['queued', 'ja-chip-enabled'],
  running: ['running', 'ja-chip-enabled'],
  done: ['done', 'ja-chip-active'],
  done_with_errors: ['done with errors', 'ja-chip-partial'],
  cancelled: ['cancelled', 'ja-chip-inactive'],
};

/** The participant's most recent fetch: status, when, and progress while it runs. */
function LastFetchCell({ job }) {
  if (!job) return <span style={{ color: 'var(--jp-gray-400)', fontSize: 12 }}>none yet</span>;
  const [label, cls] = FETCH_LABEL[job.status] || [job.status || '?', 'ja-chip-inactive'];
  const total = job.tasksTotal || 0;
  const finished = (job.tasksDone || 0) + (job.tasksFailed || 0) + (job.tasksCancelled || 0);
  const pct = total ? Math.round((finished / total) * 100) : 100;
  const active = job.status === 'queued' || job.status === 'running';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 140 }}>
      <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className={`ja-chip ${cls}`}>{label}</span>
        <span style={{ fontSize: 12, color: 'var(--jp-gray-600)' }}>
          {job.type || 'manual'} · {job.createdAt?.toDate ? job.createdAt.toDate().toLocaleDateString() : '…'}
        </span>
      </span>
      {active && (
        <div role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Fetch progress"
          style={{ height: 6, background: 'var(--jp-gray-100)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: 'var(--jp-blue-mid)' }} />
        </div>
      )}
    </div>
  );
}

LastFetchCell.propTypes = {
  job: PropTypes.shape({
    status: PropTypes.string,
    type: PropTypes.string,
    tasksTotal: PropTypes.number,
    tasksDone: PropTypes.number,
    tasksFailed: PropTypes.number,
    tasksCancelled: PropTypes.number,
    createdAt: PropTypes.object,
  }),
};

function ParticipantsGrid({
  participants, lastJobs, onInfoClick, onRefresh, isLoading,
}) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const filtered = participants.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.uid?.toLowerCase().includes(q) ||
      p.registration?.prolific?.pId?.toLowerCase().includes(q) ||
      p.health?.status?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice(page * pageSize, (page + 1) * pageSize);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(0);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="ja-input"
          style={{ maxWidth: 300 }}
          placeholder="Search participants…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
        />
        <button className="ja-btn ja-btn-ghost" onClick={onRefresh} disabled={isLoading}>
          ↻ Refresh
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--jp-gray-600)', fontFamily: 'var(--jp-mono)' }}>
          {filtered.length} participant{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Loading */}
      {isLoading && <div className="ja-progress"><div className="ja-progress-bar" /></div>}

      {/* Table */}
      <div style={{ overflowX: 'auto', border: '1px solid var(--jp-gray-200)', borderRadius: 'var(--jp-radius)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--jp-gray-50)', borderBottom: '1px solid var(--jp-gray-200)' }}>
              <th style={thStyle}>Participant ID</th>
              <th style={thStyle}>Prolific ID</th>
              <th style={thStyle}>Google Health</th>
              <th style={thStyle}>Last fetch</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--jp-gray-600)', fontSize: 13 }}>
                  {isLoading ? 'Loading…' : 'No participants found.'}
                </td>
              </tr>
            )}
            {paged.map((p, i) => {
              const prolificId = p.registration?.prolific?.pId;
              return (
                <tr
                  key={p.uid}
                  style={{
                    borderBottom: i < paged.length - 1 ? '1px solid var(--jp-gray-100)' : 'none',
                    background: '#fff',
                  }}
                >
                  <td style={{ ...tdStyle, fontFamily: 'var(--jp-mono)', fontSize: 12 }}>{p.uid}</td>
                  <td style={tdStyle}>
                    <ProlificCell prolificId={prolificId} />
                  </td>
                  <td style={tdStyle}>
                    <HealthCell health={p.health} />
                  </td>
                  <td style={tdStyle}>
                    <LastFetchCell job={lastJobs?.[p.uid || p.id]} />
                  </td>
                  <td style={tdStyle}>
                    <button className="ja-btn ja-btn-ghost" onClick={() => onInfoClick(p.uid)}>
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination + page size */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          className="ja-btn ja-btn-ghost"
          disabled={page === 0}
          onClick={() => setPage(page - 1)}
        >
          ← Prev
        </button>

        <span style={{ fontSize: 12, color: 'var(--jp-gray-600)', fontFamily: 'var(--jp-mono)', minWidth: 52, textAlign: 'center' }}>
          {totalPages === 0 ? '0 / 0' : `${page + 1} / ${totalPages}`}
        </span>

        <button
          className="ja-btn ja-btn-ghost"
          disabled={page >= totalPages - 1}
          onClick={() => setPage(page + 1)}
        >
          Next →
        </button>

        <span style={{ display: 'inline-block', width: 1, height: 18, background: 'var(--jp-gray-200)', margin: '0 4px' }} />

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--jp-gray-600)' }}>
          Rows
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{
              fontFamily: 'var(--jp-mono)', fontSize: 12,
              padding: '4px 8px', borderRadius: 6,
              border: '1px solid var(--jp-gray-200)',
              background: '#fff', color: 'var(--jp-gray-800)',
              cursor: 'pointer',
            }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
      </div>

    </div>
  );
}

const thStyle = {
  padding: '9px 14px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'var(--jp-gray-600)',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '10px 14px',
  verticalAlign: 'middle',
};

ParticipantsGrid.propTypes = {
  participants: PropTypes.array.isRequired,
  lastJobs: PropTypes.object,
  onInfoClick: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default ParticipantsGrid;
